'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/authStore';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

// Decorative counter — starts at `initial`, drifts up
function useLiveCounter(initial: number, rate: number) {
  const [n, setN] = useState(initial);
  useEffect(() => {
    const id = setInterval(() => {
      setN((x) => x + Math.max(1, Math.round(Math.random() * rate)));
    }, 900);
    return () => clearInterval(id);
  }, [rate]);
  return n;
}

// Real counter — fetches actual socket count, then drifts at rate
function useOnlineCounter(rate: number) {
  const [base, setBase] = useState<number | null>(null);
  const [delta, setDelta] = useState(0);
  useEffect(() => {
    fetch(`${API_URL}/api/stats`)
      .then((r) => r.json())
      .then((d) => setBase(typeof d.online === 'number' ? Math.max(d.online, 1) : 1))
      .catch(() => setBase(1));
  }, []);
  useEffect(() => {
    if (base === null) return;
    const id = setInterval(() => {
      setDelta((x) => x + Math.max(1, Math.round(Math.random() * rate)));
    }, 900);
    return () => clearInterval(id);
  }, [base, rate]);
  return base === null ? null : base + delta;
}

function formatNum(n: number) {
  return n.toLocaleString('en-US');
}

const FEED_SAMPLES = [
  { flag: '🇯🇵', who: 'Tokyo', msg: 'asked a new friend about 3am ramen' },
  { flag: '🇧🇷', who: 'São Paulo', msg: 'connected — first time ever' },
  { flag: '🇺🇸', who: 'NYC', msg: 'matched in 2 seconds flat' },
  { flag: '🇸🇪', who: 'Stockholm', msg: 'learned a new word in Thai' },
  { flag: '🇰🇷', who: 'Seoul', msg: 'said goodnight, hit skip' },
  { flag: '🇳🇬', who: 'Lagos', msg: 'shared a playlist' },
  { flag: '🇨🇦', who: 'Montréal', msg: 'switched to French halfway' },
  { flag: '🇮🇳', who: 'Mumbai', msg: 'dropped a movie recommendation' },
  { flag: '🇦🇺', who: 'Melbourne', msg: 'is up too late again' },
  { flag: '🇩🇪', who: 'Berlin', msg: 'ranted about the weather' },
  { flag: '🇲🇽', who: 'CDMX', msg: 'laughed for 20 minutes straight' },
  { flag: '🇪🇬', who: 'Cairo', msg: 'said hi to a stranger from Iceland' },
];

const TICKER_ITEMS = [
  { label: 'LIVE NOW', val: '2,847' },
  { label: 'TODAY', val: '412,009 chats' },
  { label: 'AVG MATCH', val: '< 3s' },
  { label: 'COUNTRIES', val: '190+' },
  { label: 'LANGUAGE', val: 'any' },
  { label: 'SIGNAL', val: 'clean' },
  { label: 'COST', val: '$0' },
  { label: 'REGISTRATION', val: 'none' },
];

const MAP_DOTS: [number, number][] = [
  [18, 35], [22, 40], [24, 30], [28, 45], [30, 38],
  [46, 30], [48, 42], [50, 36], [52, 28], [54, 46],
  [58, 34], [62, 42], [66, 38], [70, 32], [74, 44],
  [82, 58], [86, 50], [78, 62],
];

function Feed() {
  const list = useMemo(() => [...FEED_SAMPLES, ...FEED_SAMPLES], []);
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 3000);
    return () => clearInterval(id);
  }, []);

  const agoFor = (i: number) => {
    if (now === null) return '—';
    const sec = (i * 7 + ((now / 1000) | 0) % 60) % 180;
    if (sec < 10) return 'just now';
    if (sec < 60) return sec + 's ago';
    return Math.round(sec / 60) + 'm ago';
  };

  return (
    <div className="feed">
      <div className="feed-list">
        {list.map((r, i) => (
          <div key={i} className="feed-row">
            <span className="time tnum">{agoFor(i)}</span>
            <span className="who">
              <span className="flag">{r.flag}</span> <b>{r.who}</b> {r.msg}
            </span>
            <span className="tag">•LIVE</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorldMap() {
  return (
    <div className="worldmap">
      {MAP_DOTS.map(([x, y], i) => (
        <span
          key={i}
          className="worldmap-dot"
          style={{
            left: x + '%',
            top: y + '%',
            animationDelay: i * 0.15 + 's',
          }}
        />
      ))}
    </div>
  );
}

function Ticker() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ticker">
      <div className="ticker-track">
        {doubled.map((t, i) => (
          <span key={i}>
            <span style={{ color: 'var(--bs-fg-3)' }}>{t.label}</span>{' '}
            <b>{t.val}</b>
            <span className="dot">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Masthead() {
  const [time, setTime] = useState<Date | null>(null);
  useEffect(() => {
    setTime(new Date());
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const hhmm = time
    ? `${String(time.getUTCHours()).padStart(2, '0')}:${String(time.getUTCMinutes()).padStart(2, '0')}:${String(time.getUTCSeconds()).padStart(2, '0')}`
    : '--:--:--';
  const date = time ? time.toUTCString().slice(0, 16).toUpperCase() : '—';
  return (
    <div className="masthead">
      <div className="masthead-row">
        <div className="masthead-left">
          <span>STRANGER WORLD</span>
          <span>Vol.VII · Issue 182</span>
        </div>
        <div className="masthead-mid">
          <span className="live-dot"></span>
          2,847 strangers online worldwide
        </div>
        <div className="masthead-right tnum">
          <span>{date}</span>
          <span>{hhmm} UTC</span>
        </div>
      </div>
    </div>
  );
}

function UserDropdown() {
  const { isAuthenticated, user } = useAuthStore();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    clearAuth();
    router.push('/');
    setOpen(false);
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(204,255,0,0.15)',
          border: '1px solid rgba(204,255,0,0.3)',
          color: 'var(--bs-fg-1)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: 700,
        }}
        title={user?.username}
      >
        {user?.username?.charAt(0).toUpperCase() ?? 'U'}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 8,
            background: 'var(--bs-surface)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            overflow: 'hidden',
            zIndex: 1000,
            minWidth: 180,
          }}
        >
          <Link
            href="/feed"
            style={{
              display: 'block',
              padding: '12px 16px',
              color: 'var(--bs-fg-1)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              fontSize: '14px',
            }}
            onClick={() => setOpen(false)}
          >
            Feed
          </Link>
          <Link
            href="/profile"
            style={{
              display: 'block',
              padding: '12px 16px',
              color: 'var(--bs-fg-1)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              fontSize: '14px',
            }}
            onClick={() => setOpen(false)}
          >
            Settings
          </Link>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              color: 'var(--bs-fg-3)',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Logout
          </button>
        </div>
      )}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
      )}
    </div>
  );
}

function Nav() {
  const { isAuthenticated } = useAuthStore();
  const router = useRouter();

  return (
    <nav className="nav">
      <Link href="/" className="brand">
        <span className="brand-mark">S</span>
        Stranger World
      </Link>
      <div className="nav-links">
        <a href="#how">
          <span className="num">01</span>How it works
        </a>
        <a href="#safety">
          <span className="num">02</span>Safety
        </a>
        <Link href="/feed">
          <span className="num">03</span>Feed
        </Link>
        <Link href="/support">
          <span className="num">04</span>Support
        </Link>
      </div>
      <div className="nav-cta" style={{ gap: '12px', display: 'flex', alignItems: 'center' }}>
        <Link href="/chat" className="btn-primary">
          Find Stranger
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
        {isAuthenticated ? (
          <UserDropdown />
        ) : (
          <Link href="/login" className="btn-ghost">
            Sign In
          </Link>
        )}
      </div>
    </nav>
  );
}

function Hero() {
  const online = useOnlineCounter(3);
  const today = useLiveCounter(412009, 18);
  return (
    <section className="hero">
      <div className="hero-glow"></div>
      <div className="wrap">
        <div className="hero-inner">
          <div>
            <div className="eyebrow">§01 · Live feed from Earth</div>
            <h1 className="hero-headline">
              The next <span className="serif">stranger</span>
              <br />
              is already
              <br />
              typing<span className="cursor"></span>
            </h1>
            <p className="hero-lede">
              Anonymous. Instant. No profiles, no pictures, no pressure. Just the person on the
              other end of the line — from any of 190&#8239;+ countries, at any hour of any day.
              Say hello to whoever picks up next.
            </p>
            <div className="hero-actions">
              <Link href="/chat" className="btn-primary">
                Start Chatting
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
              <a href="#how" className="btn-ghost">
                How it works
              </a>
            </div>
            <div className="hero-meta">
              <div>
                Avg. match <b>&lt; 3s</b>
              </div>
              <div>
                Free <b>forever</b>
              </div>
              <div>
                No account <b>required</b>
              </div>
            </div>
          </div>

          <div className="livepanel">
            <div className="livecard">
              <div className="livecard-header">
                <span>LIVE · 01</span>
                <span className="status">
                  <span className="live-dot"></span>streaming
                </span>
              </div>
              <div className="counter-value tnum">{online === null ? '—' : formatNum(online)}</div>
              <div className="counter-sub">
                Strangers online · <span className="up">↑ +{online ? Math.round((online % 7) + 2) : 0}/min</span>
              </div>
            </div>

            <div className="livecard">
              <div className="livecard-header">
                <span>LIVE · 02 · ACTIVITY</span>
                <span className="status">
                  <span className="live-dot"></span>streaming
                </span>
              </div>
              <Feed />
            </div>

            <div className="livecard">
              <div className="livecard-header">
                <span>LIVE · 03 · GEO DISTRIBUTION</span>
                <span className="status">190+ countries</span>
              </div>
              <WorldMap />
              <div className="counter-sub tnum" style={{ marginTop: 10 }}>
                Chats today · <b style={{ color: 'var(--bs-fg-1)' }}>{formatNum(today)}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const online = useOnlineCounter(3);
  const today = useLiveCounter(412009, 14);
  const ever = useLiveCounter(1287442009, 42);
  return (
    <section>
      <div className="wrap">
        <div className="section-label">
          <span className="num">§02</span>
          <span>By the numbers · live from the wire</span>
          <span className="divider"></span>
          <span>Updated every second</span>
        </div>
      </div>
      <div className="wrap">
        <div className="stats">
          <div className="stat">
            <div className="stat-label">Online right now</div>
            <div className="stat-value tnum">{online === null ? '—' : formatNum(online)}</div>
            <div className="stat-foot">Every dot on the map is a real person waiting to talk.</div>
          </div>
          <div className="stat">
            <div className="stat-label">Chats started today</div>
            <div className="stat-value tnum">{formatNum(today)}</div>
            <div className="stat-foot">
              A new conversation begins every <b>0.21</b> seconds.
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Countries represented</div>
            <div className="stat-value tnum">
              190<span className="unit">+</span>
            </div>
            <div className="stat-foot">From Reykjavík to Riyadh. Antarctica, too.</div>
          </div>
          <div className="stat">
            <div className="stat-label">Total connections, all-time</div>
            <div className="stat-value tnum">
              {(ever / 1e9).toFixed(3)}
              <span className="unit">B</span>
            </div>
            <div className="stat-foot">
              One billion two hundred million strangers turned into&nbsp;…something.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function How() {
  return (
    <section id="how" className="how">
      <div className="wrap">
        <div className="section-label">
          <span className="num">§03</span>
          <span>How it works</span>
          <span className="divider"></span>
          <span>Three steps · no signup</span>
        </div>

        <div className="how-head">
          <div>
            <p className="eyebrow">Simple by design</p>
            <h2 className="how-title">
              Three clicks
              <br />
              from silence
              <br />
              to <em>conversation</em>.
            </h2>
          </div>
          <p className="how-sub">
            No bloat. No profile. No algorithm deciding who you&apos;d &ldquo;like&rdquo;. You press
            a button, we find a human, you talk. When you&apos;re done, you press skip and do it
            again. That&apos;s the whole product.
          </p>
        </div>

        <div className="how-steps">
          <div className="step">
            <div className="step-num">
              <span>Step 01</span>
              <span className="tag">▲ Instant</span>
            </div>
            <h3 className="step-title">
              Press the <em>button</em>.
            </h3>
            <p className="step-body">
              No account, no email, no captcha gauntlet. One tap and we start looking.
              You&apos;re not setting up — you&apos;re already in.
            </p>
            <div className="step-visual">→ avg. handshake: 0.28s</div>
          </div>
          <div className="step">
            <div className="step-num">
              <span>Step 02</span>
              <span className="tag">● Matched</span>
            </div>
            <h3 className="step-title">
              Meet a <em>stranger</em>.
            </h3>
            <p className="step-body">
              Someone on the other side of the planet just pressed the same button. Now you&apos;re
              paired. No names, no photos, no history — just a blank room with two humans in it.
            </p>
            <div className="step-visual">→ from 190+ countries, live</div>
          </div>
          <div className="step">
            <div className="step-num">
              <span>Step 03</span>
              <span className="tag">→ Skip</span>
            </div>
            <h3 className="step-title">
              Talk, then <em>vanish</em>.
            </h3>
            <p className="step-body">
              When the conversation runs out, press skip. A new one begins. Nothing is stored,
              nothing is searchable. Every chat is a ghost the moment it ends.
            </p>
            <div className="step-visual">→ end-to-end, zero logs</div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FIELD_NOTES: {
  meta: string;
  place: string;
  byline: string;
  body: string;
  hi: string;
}[] = [
  {
    meta: 'Tuesday · 03:14',
    place: 'Somewhere on Earth',
    byline: '— Anonymous, via /feed',
    body: '“I met a retired pilot from Finland, a night-shift nurse in Manila, and a nineteen-year-old in Nairobi who just got her first job.',
    hi: ' All before my coffee went cold.”',
  },
  {
    meta: 'Sunday · 23:47',
    place: 'A kitchen in Lisbon',
    byline: '— mona_k, via /feed',
    body: '“Talked to a stranger in Osaka for forty minutes about a song I hadn’t thought about in ten years.',
    hi: ' He knew every lyric.”',
  },
  {
    meta: 'Friday · 02:08',
    place: 'A bus stop in Buenos Aires',
    byline: '— n0ctambulo, via /feed',
    body: '“She was a ceramicist in Kyoto. I was a software engineer in Córdoba. We spent an hour discussing whether cats dream.',
    hi: ' Verdict: probably.”',
  },
  {
    meta: 'Wednesday · 04:33',
    place: 'A balcony in Warsaw',
    byline: '— anon_v, via /feed',
    body: '“Matched with someone who used to work the overnight shift at a radio station in Iceland.',
    hi: ' I’m still thinking about the playlist he described.”',
  },
  {
    meta: 'Monday · 01:19',
    place: 'A dorm room in Nairobi',
    byline: '— after.hours, via /feed',
    body: '“We never exchanged names. We talked for two hours about grief, and then about mangoes.',
    hi: ' I hope she’s doing okay.”',
  },
  {
    meta: 'Saturday · 03:52',
    place: 'A cab ride in Seoul',
    byline: '— late.signal, via /feed',
    body: '“A cab driver in Istanbul told me the trick to staying awake on nights like this.',
    hi: ' It was a proverb about the moon.”',
  },
];

function shuffleArr<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function Quote() {
  const [pool, setPool] = useState<typeof FIELD_NOTES | null>(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setPool(shuffleArr(FIELD_NOTES));
  }, []);

  useEffect(() => {
    if (!pool) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % pool.length);
    }, 15000);
    return () => clearInterval(id);
  }, [pool]);

  const current = pool ? pool[idx] : FIELD_NOTES[0];

  return (
    <section className="pullquote" id="safety">
      <div className="wrap">
        <div className="section-label">
          <span className="num">§05</span>
          <span>Field notes</span>
          <span className="divider"></span>
          <span>Unsolicited, unpaid, unfiltered</span>
        </div>
        <div className="pullquote-inner" key={`${idx}-${current.byline}`}>
          <div className="pullquote-meta bs-quote-swap">
            {current.meta}
            <br />
            {current.place}
            <span className="byline">{current.byline}</span>
          </div>
          <blockquote className="bs-quote-swap">
            {current.body}
            <span className="hi">{current.hi}</span>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function FeedIllustration() {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 16,
      marginBottom: 48,
    }}>
      {[
        { icon: (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccff00" strokeWidth="1.8">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ), label: 'Members', sub: 'Signed-in accounts only' },
        { icon: (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccff00" strokeWidth="1.8">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        ), label: 'Strangers', sub: 'See a locked, inaccessible view' },
        { icon: (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccff00" strokeWidth="1.8">
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
        ), label: 'Post & React', sub: 'Create and engage — members only' },
      ].map((item, i) => (
        <div key={i} style={{
          background: 'var(--bs-surface)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 14,
          padding: '1.5rem 1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'rgba(204,255,0,0.06)',
            border: '1px solid rgba(204,255,0,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {item.icon}
          </div>
          <div>
            <div style={{ color: 'var(--bs-fg-1)', fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>
              {item.label}
            </div>
            <div style={{ color: 'var(--bs-fg-3)', fontSize: '0.82rem', lineHeight: 1.5 }}>
              {item.sub}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TheFeedCTA() {
  const { isAuthenticated } = useAuthStore();
  return (
    <div className="how-head" style={{ marginTop: 48 }}>
      <p className="eyebrow" style={{ opacity: 0.7 }}>→ {isAuthenticated ? 'Welcome back' : 'Ready to join?'}</p>
      <p className="how-sub" style={{ margin: 0 }}>
        {isAuthenticated ? (
          <Link href="/feed" className="btn-primary">
            Go to the Feed
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        ) : (
          <>
            <Link href="/login" className="btn-ghost" style={{ marginRight: 10 }}>
              Create a free account
            </Link>
            <Link href="/chat" className="btn-primary">
              Or start chatting now
            </Link>
          </>
        )}
      </p>
    </div>
  );
}

function TheFeed() {
  return (
    <section className="how" id="thefeed">
      <div className="wrap">
        <div className="section-label">
          <span className="num">§04</span>
          <span>The feed · voices from the wire</span>
          <span className="divider"></span>
          <span>Members only · anonymous · unedited</span>
        </div>

        <div className="how-head">
          <div>
            <p className="eyebrow">A space for members</p>
            <h2 className="how-title">
              The feed is
              <br />
              for members
              <br />
              <em>only</em>.
            </h2>
          </div>
          <p className="how-sub">
            The feed is a members-only space — a continuous stream of moments, photographs,
            and late-night thoughts shared by signed-in strangers from every corner of the
            world. Visitors without an account see a locked view and cannot access any posts.
            Create a free account to read the full feed, post your own stories, and engage
            with the community.
          </p>
        </div>

        <FeedIllustration />

        <div className="how-steps">
          <div className="step">
            <div className="step-num">
              <span>Read 01</span>
              <span className="tag">🔒 Members</span>
            </div>
            <h3 className="step-title">
              Members <em>see the feed</em>.
            </h3>
            <p className="step-body">
              The feed is only accessible to signed-in members. Visitors without an account
              cannot view any posts — the feed remains locked until they join. Creating an
              account is free and takes under a minute.
            </p>
            <div className="step-visual">→ free account · instant access</div>
          </div>
          <div className="step">
            <div className="step-num">
              <span>Post 02</span>
              <span className="tag">◐ Members</span>
            </div>
            <h3 className="step-title">
              Members <em>publish</em>.
            </h3>
            <p className="step-body">
              Post text, photos, and late-night thoughts. Anonymous by default — the byline
              is whatever handle you choose, or none at all. No real names, no pressure.
            </p>
            <div className="step-visual">→ text + image · no real names</div>
          </div>
          <div className="step">
            <div className="step-num">
              <span>React 03</span>
              <span className="tag">♡ Members</span>
            </div>
            <h3 className="step-title">
              Members <em>react</em>.
            </h3>
            <p className="step-body">
              Like, comment, or keep scrolling. Posts appear in chronological order —
              not ranked by an algorithm. Every voice surfaces equally, every refresh.
            </p>
            <div className="step-visual">→ chronological · unranked · alive</div>
          </div>
        </div>

        <TheFeedCTA />
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="finalcta">
      <div className="wrap">
        <div className="section-label" style={{ borderTop: 'none', paddingTop: 0 }}>
          <span className="num">§06</span>
          <span>Break the silence</span>
          <span className="divider"></span>
          <span>The line is open</span>
        </div>
        <div className="finalcta-inner">
          <h2>
            Somewhere,
            <br />
            someone&apos;s about to say
            <br />
            <em>hello.</em>
          </h2>
          <p>
            The next stranger you meet could change your entire week. Find out who picks up.
          </p>
          <div className="hero-actions" style={{ justifyContent: 'center' }}>
            <Link href="/chat" className="btn-primary">
              Find Stranger
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
            <Link href="/support" className="btn-ghost">
              Read the rules first
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="bs">
      <div className="wrap">
        <div className="foot-grid">
          <div className="foot-col">
            <Link href="/" className="brand" style={{ marginBottom: 24 }}>
              <span className="brand-mark">S</span>
              Stranger World
            </Link>
            <p className="foot-blurb">
              A safe, anonymous platform to connect with people from around the world. Real
              conversations. Real connections.
            </p>
          </div>
          <div className="foot-col">
            <h4>Product</h4>
            <ul>
              <li>
                <Link href="/chat">Chat</Link>
              </li>
              <li>
                <Link href="/feed">Feed</Link>
              </li>
              <li>
                <Link href="/friends">Friends</Link>
              </li>
              <li>
                <Link href="/subscribe">Subscribe</Link>
              </li>
            </ul>
          </div>
          <div className="foot-col">
            <h4>Safety</h4>
            <ul>
              <li>
                <Link href="/support">Community rules</Link>
              </li>
              <li>
                <Link href="/support">Report</Link>
              </li>
              <li>
                <Link href="/support">Privacy</Link>
              </li>
              <li>
                <Link href="/support">Terms</Link>
              </li>
            </ul>
          </div>
          <div className="foot-col">
            <h4>Company</h4>
            <ul>
              <li>
                <Link href="/support">About</Link>
              </li>
              <li>
                <Link href="/support">Support</Link>
              </li>
              <li>
                <Link href="/support">Press kit</Link>
              </li>
              <li>
                <Link href="/support">Status</Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="foot-meta">
          <span>© 2026 Stranger World · Vol.VII</span>
          <span>Printed daily on the internet · Stay safe. Be kind.</span>
        </div>
      </div>
    </footer>
  );
}

export default function BroadsheetLanding() {
  return (
    <div className="bsheet">
      <div className="grid-backdrop"></div>
      <Masthead />
      <Ticker />
      <Nav />
      <Hero />
      <Stats />
      <How />
      <TheFeed />
      <Quote />
      <FinalCTA />
      <SiteFooter />
    </div>
  );
}
